import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, FileText, Image as ImageIcon, Video } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { MediaPicker } from "../components/MediaLibrary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContent() {
  const [blocks, setBlocks] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [pickerFor, setPickerFor] = useState(null); // key of the block currently picking
  const [pickerKind, setPickerKind] = useState("image"); // "image" | "video"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/content`, { withCredentials: true });
        setBlocks(data);
      } catch { toast.error("Chargement impossible"); }
      finally { setLoading(false); }
    })();
  }, []);

  const onChange = (key, value) => {
    setBlocks((prev) => prev.map((b) => b.key === key ? { ...b, value } : b));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const save = async (block) => {
    setSaving((s) => ({ ...s, [block.key]: true }));
    try {
      await axios.put(`${API}/admin/content/${block.key}`, { value: block.value || "" }, { withCredentials: true });
      setDirty((d) => { const c = { ...d }; delete c[block.key]; return c; });
      toast.success("Enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving((s) => ({ ...s, [block.key]: false }));
    }
  };

  // Group by page
  const byPage = blocks.reduce((acc, b) => {
    (acc[b.page] ||= []).push(b);
    return acc;
  }, {});

  return (
    <AdminLayout current="content">
      <div className="max-w-4xl" data-testid="admin-content-page">
        <div className="mb-10">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-2">CMS</p>
          <h1 className="font-serif text-4xl mb-3">Contenu du site</h1>
          <p className="text-white/50 text-sm max-w-2xl">
            Éditez ici les textes visibles publiquement. Les modifications sont appliquées immédiatement après enregistrement.
          </p>
        </div>

        {loading && <p className="text-white/40">Chargement…</p>}

        {Object.entries(byPage).map(([page, items]) => (
          <section key={page} className="mb-12">
            <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-3">
              <FileText size={16} className="text-[color:var(--gold)]" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl">{page}</h2>
            </div>
            <div className="space-y-6">
              {items.map((b) => (
                <div key={b.key} className="border border-white/10 p-5 hover:border-white/20 transition-colors" data-testid={`block-${b.key.replace(/\./g, "-")}`}>
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <label className="label-caps text-[10px] text-white/50 tracking-[0.25em]">{b.label}</label>
                    <code className="text-[9px] text-white/30 font-mono">{b.key}</code>
                  </div>

                  {b.type === "textarea" ? (
                    <textarea
                      value={b.value || ""}
                      onChange={(e) => onChange(b.key, e.target.value)}
                      rows={4}
                      className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] px-3 py-2 outline-none text-sm resize-none leading-relaxed"
                      data-testid={`input-${b.key.replace(/\./g, "-")}`}
                    />
                  ) : b.type === "image" ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          value={b.value || ""}
                          onChange={(e) => onChange(b.key, e.target.value)}
                          placeholder="URL de l'image (https://...)"
                          className="flex-1 bg-transparent border-b border-white/15 focus:border-[color:var(--gold)] py-2 outline-none text-sm"
                        />
                        <button
                          type="button"
                          data-testid={`pick-media-${b.key.replace(/\./g, "-")}`}
                          onClick={() => { setPickerKind("image"); setPickerFor(b.key); }}
                          className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black px-3 py-1.5 label-caps text-[10px] tracking-[0.2em] transition-colors"
                        >
                          <ImageIcon size={12} /> Médiathèque
                        </button>
                      </div>
                      {b.value && (
                        <img src={b.value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${b.value}` : b.value} alt="preview" className="h-24 border border-white/15" />
                      )}
                    </div>
                  ) : b.type === "video" ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          value={b.value || ""}
                          onChange={(e) => onChange(b.key, e.target.value)}
                          placeholder="URL de la vidéo (laisser vide pour utiliser l'image)"
                          className="flex-1 bg-transparent border-b border-white/15 focus:border-[color:var(--gold)] py-2 outline-none text-sm"
                        />
                        <button
                          type="button"
                          data-testid={`pick-media-${b.key.replace(/\./g, "-")}`}
                          onClick={() => { setPickerKind("video"); setPickerFor(b.key); }}
                          className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black px-3 py-1.5 label-caps text-[10px] tracking-[0.2em] transition-colors"
                        >
                          <Video size={12} /> Médiathèque
                        </button>
                        {b.value && (
                          <button
                            type="button"
                            onClick={() => onChange(b.key, "")}
                            className="border border-white/15 text-white/60 hover:text-red-400 hover:border-red-400 px-3 py-1.5 label-caps text-[10px] tracking-[0.2em]"
                            data-testid={`clear-${b.key.replace(/\./g, "-")}`}
                            title="Retirer la vidéo"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                      {b.value && (
                        <video
                          src={b.value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${b.value}` : b.value}
                          className="h-32 border border-white/15"
                          controls muted playsInline
                        />
                      )}
                      <p className="text-[10px] text-white/40 leading-relaxed">
                        Astuce : vidéo courte (5–20 s), format 16:9, poids &lt; 20 Mo pour une lecture fluide. Sur la page d'accueil, la vidéo est jouée en boucle sans son.
                      </p>
                    </div>
                  ) : (
                    <input
                      value={b.value || ""}
                      onChange={(e) => onChange(b.key, e.target.value)}
                      className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--gold)] py-2 outline-none text-sm"
                    />
                  )}

                  <div className="flex items-center justify-between mt-4">
                    {dirty[b.key] ? (
                      <span className="label-caps text-[10px] text-orange-400 tracking-[0.25em]">● Modifications non enregistrées</span>
                    ) : (
                      <span className="label-caps text-[10px] text-white/30 tracking-[0.25em]">✓ Synchronisé</span>
                    )}
                    <button
                      onClick={() => save(b)}
                      disabled={saving[b.key] || !dirty[b.key]}
                      data-testid={`save-${b.key.replace(/\./g, "-")}`}
                      className="inline-flex items-center gap-2 bg-[color:var(--gold)] text-black px-4 py-2 label-caps text-[10px] tracking-[0.25em] hover:bg-white transition-colors disabled:opacity-40"
                    >
                      {saving[b.key] ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      Enregistrer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <MediaPicker
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        onSelect={(url) => {
          if (pickerFor) onChange(pickerFor, url);
        }}
        kind={pickerKind}
      />
    </AdminLayout>
  );
}
